/**
 * フォーカス管理用カスタムフック
 * アクセシビリティ対応のフォーカス制御機能を提供
 */

import { useRef, useCallback, useEffect } from 'react';

export interface FocusManagementOptions {
  autoFocus?: boolean;
  trapFocus?: boolean;
  restoreFocus?: boolean;
  initialDelay?: number;
}

export const useFocusManagement = (options: FocusManagementOptions = {}) => {
  const {
    autoFocus = true,
    trapFocus = false,
    restoreFocus = false,
    initialDelay = 100,
  } = options;

  const containerRef = useRef<HTMLElement>(null);
  const previousActiveElement = useRef<Element | null>(null);

  // フォーカス可能な要素のセレクター
  const focusableSelector = [
    'input:not([disabled]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'a[href]:not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable]:not([contenteditable="false"])',
  ].join(', ');

  // フォーカス可能要素を取得
  const getFocusableElements = useCallback(() => {
    if (!containerRef.current) return [];
    const elements = containerRef.current.querySelectorAll(focusableSelector);
    return Array.from(elements) as HTMLElement[];
  }, [focusableSelector]);

  // 最初の要素にフォーカス
  const focusFirst = useCallback(() => {
    const elements = getFocusableElements();
    if (elements.length > 0) {
      elements[0].focus();
      return true;
    }
    return false;
  }, [getFocusableElements]);

  // 最後の要素にフォーカス
  const focusLast = useCallback(() => {
    const elements = getFocusableElements();
    if (elements.length > 0) {
      elements[elements.length - 1].focus();
      return true;
    }
    return false;
  }, [getFocusableElements]);

  // 次の要素にフォーカス
  const focusNext = useCallback(() => {
    const elements = getFocusableElements();
    const currentIndex = elements.findIndex(el => el === document.activeElement);
    
    if (currentIndex === -1) {
      return focusFirst();
    }
    
    const nextIndex = (currentIndex + 1) % elements.length;
    elements[nextIndex].focus();
    return true;
  }, [getFocusableElements, focusFirst]);

  // 前の要素にフォーカス
  const focusPrevious = useCallback(() => {
    const elements = getFocusableElements();
    const currentIndex = elements.findIndex(el => el === document.activeElement);
    
    if (currentIndex === -1) {
      return focusLast();
    }
    
    const previousIndex = currentIndex === 0 ? elements.length - 1 : currentIndex - 1;
    elements[previousIndex].focus();
    return true;
  }, [getFocusableElements, focusLast]);

  // 特定の要素にフォーカス
  const focusElement = useCallback((selector: string) => {
    if (!containerRef.current) return false;
    const element = containerRef.current.querySelector(selector) as HTMLElement;
    if (element) {
      element.focus();
      return true;
    }
    return false;
  }, []);

  // エラー時のフォーカス復帰
  const focusError = useCallback((errorSelector?: string) => {
    if (errorSelector && focusElement(errorSelector)) {
      return true;
    }
    // エラー要素が見つからない場合は最初の入力フィールドにフォーカス
    return focusElement('input, select, textarea') || focusFirst();
  }, [focusElement, focusFirst]);

  // キーボードナビゲーション処理
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!trapFocus) return;

    switch (e.key) {
      case 'Tab':
        e.preventDefault();
        if (e.shiftKey) {
          focusPrevious();
        } else {
          focusNext();
        }
        break;
      case 'Escape':
        if (restoreFocus && previousActiveElement.current) {
          (previousActiveElement.current as HTMLElement).focus();
        }
        break;
    }
  }, [trapFocus, restoreFocus, focusNext, focusPrevious]);

  // フォーカストラップの初期化
  useEffect(() => {
    if (!containerRef.current) return;

    // 現在のアクティブ要素を保存
    if (restoreFocus) {
      previousActiveElement.current = document.activeElement;
    }

    // 自動フォーカス
    if (autoFocus) {
      const timer = setTimeout(() => {
        focusFirst();
      }, initialDelay);

      return () => clearTimeout(timer);
    }
  }, [autoFocus, restoreFocus, initialDelay, focusFirst]);

  // フォーカストラップのイベントリスナー
  useEffect(() => {
    if (!trapFocus || !containerRef.current) return;

    const container = containerRef.current;
    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [trapFocus, handleKeyDown]);

  return {
    containerRef,
    focusFirst,
    focusLast,
    focusNext,
    focusPrevious,
    focusElement,
    focusError,
    getFocusableElements,
  };
};

export default useFocusManagement;